import React from "react";
import { useTranslation } from "react-i18next";
import { Box, Grid, Rating, Tooltip } from "@mui/material";
import Typography from "@mui/material/Typography";
import { themes } from "theme/themes";
import { brandingInfo } from "features/server/brandingSlice";
import { useSelector } from "react-redux";
import { selectUserInfo } from "features/user/userSlice";
// import Divider from '@mui/material/Divider';
import AttachFileOutlinedIcon from '@mui/icons-material/AttachFileOutlined';
import { ReviewResponse } from "subModule/src/services/invoice/interface";
interface cardSummaryProps {
  data: any;
  typeName?: string;
  review?: ReviewResponse;
}

const CardSummary: React.FC<cardSummaryProps> = ({ data, typeName, review }) => {
  const { t } = useTranslation();
  const loggedInUserCurrency = useSelector(selectUserInfo)?.business?.currency;
  const branding = useSelector(brandingInfo);
  const themeColors =
    branding?.attributes?.themeId === 100
      ? branding?.attributes?.customTheme
      : (themes as any)[branding?.attributes?.themeId || 3];
  return (
    <Grid container className={`mt-4 flex ${(typeName === 'jobCard' && data?.url) || (data?.status === 202 || data?.status === 304) || (typeName === 'invoice' && review?.rating) ? 'justify-between' : 'justify-end'}`}>
      {typeName === 'jobCard' && data?.url ? <Grid item xs={12} md={6} lg={3}>
        <Typography className="font-Saira text-black text-start font-[600] text-[14px] leading-[30px] ms-[15px]">
          {t("attachment")}
        </Typography>
        <Tooltip title={data?.url || ''} placement="top" enterDelay={0}>
          <Box
            className='bg-[#E4D9E6] p-4 rounded-[8px] ms-[15px] flex gap-1 justify-start items-center cursor-pointer'
            onClick={() => window.open(`${data?.url || ''}`, '_blank')}
          >
            <AttachFileOutlinedIcon sx={{ transform: 'rotateZ(45deg)', fontSize: '31px' }} />
            <span className="font-Saira text-[13px] font-semibold text-ellipsis overflow-hidden w-[205px] whitespace-nowrap">{data?.url?.split('/')?.pop() || ''}</span>
          </Box>
        </Tooltip>
      </Grid> : null}
      {typeName === 'invoice' && review?.rating ? <Grid item xs={12} md={6} lg={3}>
        <Box className='bg-white rounded-[8px] p-[20px] ms-[15px] w-[290px]'>
          <Rating
            name="simple-controlled"
            value={review?.rating || 0}
            readOnly
            sx={{
              '& .MuiRating-iconFilled svg': { fill: themeColors?.secondary },
              '& .MuiRating-iconEmpty svg': { fill: themeColors?.secondary }
            }}
          />
          <Typography className="font-Saira font-semibold flex gap-1 text-[15px] text-black justify-center">
            <span>{(review?.rating || 0)?.toFixed(1)}</span>
            <span>/</span>
            <span>5.0</span>
            <span>{t('rating')}</span>
          </Typography>
          <Typography className="text-[11px] text-center font-Saira leading-2 italic mt-[8px]">
            {review?.comment || ''}
          </Typography>
        </Box>
      </Grid> : null}
      {(data?.status === 202 || data?.status === 304) ? <Grid item xs={12} md={6} lg={3}>
        <Box className='font-Saira text-start'>
          <span className="font-semibold">{t('Reason')}: </span>{' '}
          <span>{data?.reasonTitle || ''}</span>
        </Box>
        <Box className="flex font-Saira">
          <span className="font-semibold">{t('notes')}: </span>{' '}
          <Tooltip title={data?.reasonComment || ''}>
            <span
              style={{
                maxWidth: '100%',
                overflow: 'hidden',
                whiteSpace: 'nowrap',
                textOverflow: 'ellipsis',
                display: 'block',
                marginLeft: '4px',
              }}
            >{data?.reasonComment || ''}</span>
          </Tooltip>
        </Box>
      </Grid> : null}
      <Grid
        item
        xs={12}
        md={6}
        lg={3}
        className="rounded-xl p-2"
        style={{ background: themeColors?.secondary }}
      >
        <Grid
          item
          xs={12}
          className=" border-b flex items-center justify-between border-white"
        >
          <Typography className="font-Saira text-white text-start font-[400] text-[11px] leading-[30px]">
            {t("subTotal")}
          </Typography>
          <Typography className="font-Saira text-white text-start font-[400] text-[11px] leading-[30px]">
            <span className="uppercase me-[3px]">{loggedInUserCurrency || ''}</span>
            {new Intl.NumberFormat("en-US", {}).format(data?.subtotalAmount || 0)}
          </Typography>
        </Grid>
        {data?.scrapValue && (
          <Grid
            item
            xs={12}
            className=" border-b flex items-center justify-between border-white"
          >
            <Typography className="font-Saira text-white text-start font-[400] text-[11px] leading-[30px]">
              {t("scrapValue")}
            </Typography>
            <Typography className="font-Saira text-white text-start font-[400] text-[11px] leading-[30px]">
              <span className="uppercase me-[3px]">{loggedInUserCurrency || ''}</span>
              {new Intl.NumberFormat("en-US", {}).format(data?.scrapValue)}
            </Typography>
          </Grid>
        )}
        <Grid
          item
          xs={12}
          className=" border-b flex items-center justify-between border-white"
        >
          <Typography className="font-Saira text-white italic text-start font-[500] text-[11px] leading-[30px]">
            {t("Discount")}
          </Typography>
          <Typography className="font-Saira text-white italic text-start font-[500] text-[11px] leading-[30px]">
            <span className="uppercase me-[3px]">{loggedInUserCurrency || ''}</span>
            {new Intl.NumberFormat("en-US", {}).format(data?.discount || 0)}
          </Typography>
        </Grid>
        <Grid
          item
          xs={12}
          className=" border-b flex items-center justify-between border-white"
        >
          <Typography className="font-Saira text-white italic text-start font-[500] text-[11px] leading-[30px]">
            {t("discountedSubtotal")}
          </Typography>
          <Typography className="font-Saira text-white italic text-start font-[500] text-[11px] leading-[30px]">
            <span className="uppercase me-[3px]">{loggedInUserCurrency || ''}</span>
            {new Intl.NumberFormat("en-US", {}).format(parseFloat(data?.discSubTotal || 0))}
          </Typography>
        </Grid>
        <Grid
          item
          xs={12}
          className=" border-b flex items-center justify-between border-white"
        >
          <Typography className="font-Saira text-white text-start font-[400] text-[11px] leading-[30px]">
            {t("tax")}
          </Typography>
          <Typography className="font-Saira text-white text-start font-[400] text-[11px] leading-[30px]">
            <span className="uppercase me-[3px]">{loggedInUserCurrency || ''}</span>
            {new Intl.NumberFormat("en-US", {}).format(data?.taxAmount || 0)}
          </Typography>
        </Grid>
        <Grid item xs={12} className=" flex items-center justify-between">
          <Typography
            className="font-Saira text-start font-[600] text-[14px] leading-[30px]"
            sx={{ color: typeName === 'invoice' ? '#fff' : themeColors?.primary }}
          >
            {t("totalWithTax")}
          </Typography>
          <Typography className="font-Saira text-white text-start font-[600] text-[13px] leading-[30px]">
            <span className="uppercase me-[3px]">{loggedInUserCurrency || ''}</span>
            {new Intl.NumberFormat("en-US", {}).format(data?.totalAmount || 0)}
          </Typography>
        </Grid>
        {typeName === "invoice" ? <>
          <Grid item xs={12} className=" flex items-center justify-between">
            <Typography
              className="font-Saira text-start font-[600] text-[14px] leading-[30px]"
              sx={{ color: themeColors?.primary }}
            >
              {t("CREDIT NOTE")}
            </Typography>
            <Typography className="font-Saira text-white text-start font-[600] text-[13px] leading-[30px]">
              <span className="uppercase me-[3px]">{loggedInUserCurrency || ''}</span>
              {new Intl.NumberFormat("en-US", {}).format(data?.creditAmount || 0)}
            </Typography>
          </Grid>
          <Grid item xs={12} className=" flex items-center justify-between">
            <Typography
              className="font-Saira text-start font-[600] text-[14px] leading-[30px]"
              sx={{ color: themeColors?.primary }}
            >
              {t("paidAmount")}
            </Typography>
            <Typography className="font-Saira text-white text-start font-[600] text-[13px] leading-[30px]">
              <span className="uppercase me-[3px]">{loggedInUserCurrency || ''}</span>
              {new Intl.NumberFormat("en-US", {}).format((data?.paidAmount || 0))}
            </Typography>
          </Grid>
          <Grid item xs={12} className=" flex items-center justify-between">
            <Typography
              className="font-Saira text-start font-[600] text-[14px] leading-[30px]"
              sx={{ color: themeColors?.primary }}
            >
              {t("dueAmount")}
            </Typography>
            <Typography className="font-Saira text-white text-start font-[600] text-[13px] leading-[30px]">
              <span className="uppercase me-[3px]">{loggedInUserCurrency || ''}</span>
              {new Intl.NumberFormat("en-US", {}).format(data?.balanceAmount || 0)}
            </Typography>
          </Grid>
        </> : null}
      </Grid>
    </Grid>
  );
};

export default CardSummary;
